import React, { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { CalendarX2, Mail, MapPin, Navigation, Phone, Store } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import EventCard from '@/components/EventCard';
import { EmptyState, Img, Rating, Shimmer } from '@/components/ui';
import { useEventList, useVenues } from '@/hooks/useCatalog';
import { isUpcoming } from '@/lib/filters';

/**
 * Everything on at one partner venue. Provider details and its live event list
 * both come from the API, so an admin toggling a venue offline empties it here.
 */
const VenueDetails = () => {
  const { id } = useParams();
  const { venues, loading: venuesLoading } = useVenues();
  const { events, loading: eventsLoading } = useEventList();

  const venue = useMemo(() => venues.find((v) => String(v.id) === String(id)), [venues, id]);
  const venueEvents = useMemo(
    () =>
      events
        .filter((e) => String(e.providerId) === String(id))
        .sort((a, b) => String(a.startDate || '9').localeCompare(String(b.startDate || '9'))),
    [events, id]
  );

  const upcoming = venueEvents.filter(isUpcoming);
  const past = venueEvents.filter((e) => !isUpcoming(e));
  const loading = venuesLoading || eventsLoading;

  if (loading) {
    return (
      <div className="min-h-screen bg-surface-muted">
        <Header />
        <Shimmer className="h-56 w-full rounded-none sm:h-72" />
        <div className="lk-container space-y-4 py-8">
          <Shimmer className="h-8 w-64" />
          <Shimmer className="h-4 w-96" />
        </div>
      </div>
    );
  }

  if (!venue) {
    return (
      <div className="flex min-h-screen flex-col bg-surface-muted">
        <Header />
        <div className="lk-container flex-1 py-20">
          <EmptyState
            icon={Store}
            title="Venue not found"
            message="This venue may no longer be listed on LocalKonnect."
            action={
              <Link to="/" className="lk-btn-primary">
                Browse all events
              </Link>
            }
          />
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-surface-muted">
      <Header />

      <main className="flex-1">
        {/* Cover */}
        <div className="relative">
          <Img src={venue.cover} alt="" label={venue.name} monogram={false} className="h-56 w-full sm:h-72" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/40 to-transparent" />
          <div className="lk-container absolute inset-x-0 bottom-0 pb-6">
            <div className="flex items-end gap-4">
              <Img
                src={venue.logo}
                alt=""
                label={venue.name}
                className="hidden h-20 w-20 shrink-0 rounded-2xl shadow-cardHover ring-4 ring-white/25 sm:block"
              />
              <div className="min-w-0 pb-1 text-white">
                <h1 className="text-h1">{venue.name}</h1>
                <p className="mt-1.5 flex items-start gap-1.5 text-sm text-white/80">
                  <MapPin className="mt-0.5 h-4 w-4 shrink-0" />
                  <span className="line-clamp-2">{venue.address}</span>
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="lk-container py-8">
          <div className="grid gap-8 lg:grid-cols-3">
            <div className="lg:col-span-2">
              <h2 className="lk-heading mb-1">
                {upcoming.length > 0 ? 'Upcoming at this venue' : 'Nothing on right now'}
              </h2>
              <p className="mb-6 text-sm text-ink-500">
                {upcoming.length} live listing{upcoming.length === 1 ? '' : 's'}
              </p>

              {upcoming.length === 0 ? (
                <EmptyState
                  icon={CalendarX2}
                  title="No live events here yet"
                  message="This venue hasn't published an upcoming event. Check back soon."
                  action={
                    <Link to="/" className="lk-btn-primary">
                      Explore other events
                    </Link>
                  }
                />
              ) : (
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
                  {upcoming.map((event, i) => (
                    <EventCard key={event.id} event={event} index={i} compact />
                  ))}
                </div>
              )}

              {past.length > 0 && (
                <div className="mt-10">
                  <h3 className="mb-4 text-lg font-bold text-ink-900">Past events</h3>
                  <div className="grid grid-cols-2 gap-4 opacity-70 sm:grid-cols-3">
                    {past.slice(0, 6).map((event, i) => (
                      <EventCard key={event.id} event={event} index={i} compact />
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Venue info rail */}
            <aside className="lg:col-span-1">
              <div className="lk-card sticky top-24 p-6">
                <h2 className="mb-4 text-lg font-bold text-ink-900">Venue details</h2>

                {venue.rating > 0 && (
                  <div className="mb-4 rounded-xl bg-surface-muted p-4">
                    <Rating value={venue.rating} count={venue.totalReviews} />
                    <p className="mt-1 text-xs text-ink-500">
                      Based on {venue.totalReviews} LocalKonnect review
                      {venue.totalReviews === 1 ? '' : 's'}
                    </p>
                  </div>
                )}

                {venue.description && (
                  <p className="mb-5 text-sm leading-relaxed text-ink-700">{venue.description}</p>
                )}

                <dl className="space-y-4 text-sm">
                  <div className="flex gap-3">
                    <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-brand-secondary" />
                    <div>
                      <dt className="lk-label">Address</dt>
                      <dd className="text-ink-900">{venue.address || 'Not provided'}</dd>
                      {venue.distanceKm != null && (
                        <dd className="mt-0.5 text-xs text-ink-500">{venue.distanceKm} km from you</dd>
                      )}
                    </div>
                  </div>

                  {venue.phone && (
                    <div className="flex gap-3">
                      <Phone className="mt-0.5 h-4 w-4 shrink-0 text-brand-secondary" />
                      <div>
                        <dt className="lk-label">Phone</dt>
                        <dd>
                          <a href={`tel:${venue.phone}`} className="text-ink-900 hover:text-brand-secondary">
                            {venue.phone}
                          </a>
                        </dd>
                      </div>
                    </div>
                  )}

                  {venue.email && (
                    <div className="flex gap-3">
                      <Mail className="mt-0.5 h-4 w-4 shrink-0 text-brand-secondary" />
                      <div className="min-w-0">
                        <dt className="lk-label">Email</dt>
                        <dd className="truncate">
                          <a href={`mailto:${venue.email}`} className="text-ink-900 hover:text-brand-secondary">
                            {venue.email}
                          </a>
                        </dd>
                      </div>
                    </div>
                  )}
                </dl>

                {venue.latitude && venue.longitude && (
                  <a
                    href={`https://www.google.com/maps/dir/?api=1&destination=${venue.latitude},${venue.longitude}`}
                    target="_blank"
                    rel="noreferrer"
                    className="lk-btn-ghost mt-6 w-full"
                  >
                    <Navigation className="h-4 w-4" />
                    Get directions
                  </a>
                )}
              </div>
            </aside>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default VenueDetails;
